import { reportFailure } from 'utils/reportFailure';

/**
 * Runs an async task that nothing can wait for, such as a lifecycle method's loading or an event
 * handler, and tells the user if it fails.
 *
 * React ignores what `componentDidMount` or an `onClick` returns, so a rejection there used to
 * be unhandled: nothing on screen, and at most a line in the console. Most of what these tasks
 * call now reports its own failure and resolves (#193 onwards); this catches whatever still
 * rejects.
 */
export const handled = (task: Promise<unknown>): void => {
  task.catch(reportFailure);
};

/**
 * An async function as React wants an event handler: one that returns nothing. It runs `fn`
 * through `handled`, so a failure is reported instead of left unhandled.
 *
 * `onClick={handler(this.save)}` rather than `onClick={this.save}`: React drops the promise an
 * async handler returns, and a rejection with it (#220).
 */
export const handler = <A extends Array<unknown>>(fn: (...args: A) => Promise<unknown> | void) =>
  (...args: A): void => {
    // Some callbacks are typed to return either, like the action menu's items.
    const result = fn(...args);
    if (result) {
      handled(result);
    }
  };
